import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import SlideToggle from 'garaje/components/slide-toggle';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type CompanyConfigurationModel from 'garaje/models/company-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

const TITLE = 'Allow admins to bypass invite approvals';

interface AdminApprovalBypassSignature {
  Args: {
    /** Undefined while `@isLoading`. */
    companyConfiguration?: CompanyConfigurationModel;
    isLoading?: boolean;
  };
  Element: HTMLDivElement;
}

export default class AdminApprovalBypass extends Component<AdminApprovalBypassSignature> {
  @service declare abilities: AbilitiesService;
  @service declare flashMessages: FlashMessagesService;

  get isReadOnly(): boolean {
    return this.abilities.cannot('manage-visitor-settings manage-settings');
  }

  get toggleDisabled(): boolean {
    return this.saveTask.isRunning || this.isReadOnly;
  }

  saveTask = dropTask(async (enabled: boolean) => {
    const companyConfiguration = this.args.companyConfiguration;
    if (!companyConfiguration || this.isReadOnly) return;
    companyConfiguration.adminApprovalBypassEnabled = enabled;

    try {
      await companyConfiguration.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      companyConfiguration.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  onToggle = (checked: boolean): void => {
    void this.saveTask.perform(checked);
  };

  <template>
    <SettingsPanel @isLoading={{@isLoading}} ...attributes data-test-admin-approval-bypass as |panel|>
      <panel.header
        @iconUri="/assets/images/features/auto-sign-in.svg"
        @title={{TITLE}}
        @description="Allow admins with approval permissions to skip approvals for their own invites"
        @showCancel={{false}}
        as |header|
      >
        <header.controls>
          <SlideToggle
            data-test-admin-approval-bypass-toggle
            role="switch"
            aria-label={{TITLE}}
            aria-checked={{if @companyConfiguration.adminApprovalBypassEnabled "true" "false"}}
            aria-disabled={{if this.isReadOnly "true"}}
            aria-busy={{if this.saveTask.isRunning "true"}}
            @isChecked={{@companyConfiguration.adminApprovalBypassEnabled}}
            @isDisabled={{this.toggleDisabled}}
            @onClick={{this.onToggle}}
          />
        </header.controls>
      </panel.header>
    </SettingsPanel>
  </template>
}
